// Polaris React holdouts — Skeleton* (no `s-*` skeleton web component exists).
// Same reason PageSkeleton.jsx / DashboardSkeleton.jsx use them; both survive the
// CSS purge in vite-plugins/ns-polaris-css-purge.mjs.
import { SkeletonBodyText, SkeletonDisplayText } from "@shopify/polaris";

import { useAdminLoading } from "../utils/adminLoading";

/**
 * The loading picture for /billing, defined ONCE and rendered at both of that
 * route's gates: the Suspense fallback in components/App.jsx (chunk downloading)
 * and the `if (fetching)` branch in routes/billing.jsx:462 (shop query in flight).
 *
 * It exists as its own component rather than two inline call sites precisely so the
 * two gates CANNOT drift apart — see the header of PageSkeleton.jsx for the problem
 * this solves (/billing showed a large spinner then a smaller one for one wait).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS A PORTRAIT OF /billing, NOT A GENERIC PAGE.
 * ─────────────────────────────────────────────────────────────────────────────
 * It used to be `<PageSkeleton heading="Billing & Plans" sections={[3, 6]} />` —
 * two anonymous grey blobs whose shape had nothing to do with what landed, so the
 * handoff still shoved the viewport. Every line below mirrors a specific line of
 * routes/billing.jsx; the file:line references are load-bearing, keep them current.
 *
 *   billing.jsx:496  <s-page heading="Billing & Plans">   ← no inlineSize, so none here
 *   billing.jsx:497  <s-stack direction="block" gap="base">
 *   billing.jsx:556  the UNCONDITIONAL "we now count popups shown" info banner
 *   billing.jsx:564  usage <s-section> — heading row, <s-divider>, 2-tile grid
 *   billing.jsx:620  usage grid `repeat(auto-fit, minmax(220px, 1fr))`
 *   billing.jsx:422  the 220px × 6px usage bar (a plain role="progressbar" div —
 *                    this repo has no s-progress-bar)
 *   billing.jsx:685  plans <s-section> — heading, blurb, card grid, one button
 *   billing.jsx:706  plan grid `repeat(auto-fit, minmax(180px, 1fr))`
 *   billing.jsx:779  the single primary "Change your plan" button
 *
 * ⚠️ `heading` must stay byte-identical to billing.jsx:496's own
 * `<s-page heading="Billing & Plans">`, or the heading visibly changes at handoff.
 * The grid `gridTemplateColumns` strings must stay identical too — they are what
 * makes the tiles and cards land in the same columns they were drawn in.
 *
 * DELIBERATELY OMITTED:
 *  • The four conditional banners (billing.jsx:498 trial, :510 cancelled,
 *    :518 frozen, :535 no-pricing-URL). Reserving space for a banner that most
 *    merchants never see manufactures the jump this file exists to remove.
 *  • The badges at :574/:585 and the "Usage resets on …" date at :605 — all
 *    conditional on shopData fields; only the "Current usage" heading is certain.
 *  • The AI tile's usage bar (:662) — it is skipped entirely when the AI limit is
 *    null ("Unlimited"), so only the popups tile's bar (:643) is unconditional.
 *    That one always draws, because `impressionLimit` is resolved through
 *    PLAN_CATALOGUE and can never be null (see billing.jsx:466-489).
 *  • `<s-section heading="Compare features">` (:792) and everything after it —
 *    it sits below four plan cards and a button, i.e. below the fold. Drawing
 *    off-screen skeleton costs paint and buys nothing.
 */

/**
 * `PLAN_CATALOGUE.length` in web/utils/planDisplayNames.js — free / Growth /
 * Scale / Pro. NOT imported: this component is in App.jsx's EAGER graph (it is a
 * Suspense fallback), and importing would drag ~7 KB of plan data and feature
 * copy into the boot chunk to learn one integer. Keep in step by hand.
 */
const PLAN_CARD_COUNT = 4;

/**
 * The 220 × 6 px usage bar from billing.jsx:419-448, drawn empty.
 *
 * Geometry copied exactly (maxInlineSize 220px, 6px tall, fully rounded, the same
 * #e1e3e5 track) so the real bar lands on the pixels this reserved. No
 * `role="progressbar"` — a progressbar with nothing to report would announce a
 * false zero to a screen reader; this is decoration for the duration of a wait.
 */
function UsageBarPlaceholder() {
  return (
    <s-box maxInlineSize="220px">
      <div
        aria-hidden="true"
        style={{
          width: "100%",
          height: "6px",
          backgroundColor: "#e1e3e5",
          borderRadius: "999px",
        }}
      />
    </s-box>
  );
}

export default function BillingSkeleton() {
  // Drive Shopify's own header progress bar for as long as this is mounted. This
  // is what PageSkeleton did for us before; dropping PageSkeleton without this
  // call would silently kill the indicator for /billing. Zero-arg and
  // unconditional by design — see utils/adminLoading.js.
  useAdminLoading();

  return (
    <s-page heading="Billing & Plans">
      <s-stack direction="block" gap="base">
        {/* billing.jsx:556 — the one banner that is always there. */}
        <s-box padding="base" background="subdued" borderRadius="base">
          <s-stack direction="block" gap="small">
            <SkeletonDisplayText size="small" />
            <SkeletonBodyText lines={2} />
          </s-stack>
        </s-box>

        {/* billing.jsx:564 — usage panel (unheaded section). */}
        <s-section>
          <s-stack direction="block" gap="base">
            {/* :566 heading row. Left cell only — the right cell (:605) and the
                badges (:574, :585) are all conditional. */}
            <s-stack
              direction="inline"
              justifyContent="space-between"
              alignItems="center"
              gap="small"
            >
              <SkeletonDisplayText size="small" />
            </s-stack>

            {/* :618 */}
            <s-divider />

            {/* :620 — same auto-fit track as the real grid. */}
            <s-grid
              gridTemplateColumns="repeat(auto-fit, minmax(220px, 1fr))"
              gap="base"
            >
              {/* :632 Popups shown — label, value, bar. */}
              <s-stack direction="block" gap="small">
                <SkeletonBodyText lines={2} />
                <UsageBarPlaceholder />
              </s-stack>

              {/* :647 AI generations — label, value. No bar (see header). */}
              <s-stack direction="block" gap="small">
                <SkeletonBodyText lines={2} />
              </s-stack>
            </s-grid>
          </s-stack>
        </s-section>

        {/* billing.jsx:685 — plans panel (unheaded section). */}
        <s-section>
          <s-stack direction="block" gap="base">
            {/* :687 "Plans & pricing" */}
            <SkeletonDisplayText size="small" />
            {/* :688 the "your plan is managed by Shopify" blurb, ~2 lines. */}
            <SkeletonBodyText lines={2} />

            {/* :706 — same auto-fit track as the real card grid. */}
            <s-grid
              gridTemplateColumns="repeat(auto-fit, minmax(180px, 1fr))"
              gap="base"
            >
              {Array.from({ length: PLAN_CARD_COUNT }, (_, i) => (
                // :713 — same box treatment as a real plan card, so the card
                // chrome does not appear from nowhere at handoff.
                <s-box
                  key={i}
                  padding="base"
                  background="base"
                  border="base"
                  borderRadius="base"
                >
                  <s-stack direction="block" gap="small">
                    {/* :727 name (+ conditional pill, not drawn) */}
                    <SkeletonBodyText lines={1} />
                    {/* :745 price row */}
                    <SkeletonDisplayText size="small" />
                    {/* :755 one-line description */}
                    <SkeletonBodyText lines={1} />
                  </s-stack>
                </s-box>
              ))}
            </s-grid>

            {/* :778 — the page's only button, always rendered. */}
            <s-stack direction="inline" justifyContent="start">
              <s-box
                inlineSize="148px"
                blockSize="28px"
                background="subdued"
                borderRadius="base"
              />
            </s-stack>
          </s-stack>
        </s-section>
      </s-stack>
    </s-page>
  );
}
